import { DragEvent, useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Grid, IconButton, List, ListItem, ListItemIcon, Typography, Divider } from '@mui/material';
import { CloudUpload, Delete, InsertDriveFile, UploadFileRounded } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { formatBytes } from 'src/shared/functions/formatBytes';
import { Controller, useFormContext } from 'react-hook-form';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import {
  FileValidationModeEnum,
  checkFileExtension,
  checkMimeType,
  getBase64MimeType,
  getFileAsBase64,
  validMimeTypes,
} from 'src/shared/functions/fileExtensions';
import { DocumentResponse } from 'src/modules/documentArchive/models/documentResponse';

type FileDropZoneProps = {
  disabled?: boolean;
  allowMultiple?: boolean;
  showFileHeader?: boolean;
  orientation?: 'horizontal' | 'vertical';
  fileValidationMode?: FileValidationModeEnum;
  onFilesChange: (files: File[]) => void;
  onDocumentFileRemove?: (fileId: string) => void;
  documentFiles?: DocumentResponse[] | undefined;
};

const FileDropZone = (props: FileDropZoneProps) => {
  const { t } = useTranslation(['common']);
  const {
    disabled = false,
    allowMultiple = false,
    showFileHeader = false,
    orientation = 'horizontal',
    fileValidationMode = FileValidationModeEnum.Normal,
    onFilesChange,
    onDocumentFileRemove = undefined,
    documentFiles = undefined,
  } = props;
  const [validFiles, setValidFiles] = useState<File[]>([]);
  const [, setInvalidFiles] = useState<File[]>([]); // TODO: if we want to show a file list with invalid files..
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    onFilesChange(validFiles);
  }, [onFilesChange, validFiles]);

  const dragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const dragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const validateFile = (file: File) => {
    // ConsoleHelper.log(file);
    return checkMimeType(file.type) && checkFileExtension(file.name);
  };

  const validateFiles = async (files: FileList) => {
    if (files.length > 1 && !allowMultiple) {
      // Message: Only one file at a time allowed
      toast.warn(<>{t('common:fileDropZone.OnlyOneFileAtATimeAllowed')}</>);
      return;
    }
    if (validFiles.length >= 1 && !allowMultiple) {
      // Message: Only one file allowed
      toast.warn(<>{t('common:fileDropZone.OnlyOneFileAllowed')}</>);
      return;
    }

    if (fileValidationMode === FileValidationModeEnum.IncomingBankPayment) {
      const fileArray = Array.from(files); // clone because input reset clears FileList
      const promiseArray: Promise<any>[] = fileArray.map((file) => getFileAsBase64(file));

      Promise.allSettled(promiseArray).then((results) => {
        const fulfilled = (results.filter((r) => r.status === 'fulfilled') as PromiseFulfilledResult<any>[]).map((r) => r.value);
        fulfilled.forEach((item: any) => {
          const res = getBase64MimeType(item);
          ConsoleHelper.log('base64 type', res, item, results, fulfilled);
        });
        setValidFiles((prevArray) => [...prevArray, ...fileArray]);
      });
    }

    if (fileValidationMode === FileValidationModeEnum.Normal) {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        if (validateFile(files[i])) {
          // add to an array so we can display the name of file
          setValidFiles((prevArray) => [...prevArray, file]);
        } else {
          setInvalidFiles((prevArray) => [...prevArray, file]);
          toast.warn(`${t('common:fileDropZone.InvalidFile')}: ${file.name}`);
        }
      }
    }
  };

  const fileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const { files } = e.dataTransfer;
    validateFiles(files);
  };

  const changeHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const { files } = e.target;
    if (files) validateFiles(files);
    e.currentTarget.value = '';
  };

  const removeFile = useCallback(
    (name: string) => {
      const validFileIndex = validFiles.findIndex((e) => e.name === name);
      validFiles.splice(validFileIndex, 1);
      setValidFiles([...validFiles]);
      if (inputRef.current) inputRef.current.value = '';
    },
    [validFiles],
  );

  const fileCounter = () => {
    let count = validFiles.length;
    if (documentFiles !== undefined) {
      count += documentFiles.length;
      // ConsoleHelper.log(documentFiles);
    }
    return count;
  };

  const renderList = () => {
    return (
      <List dense sx={{ maxHeight: 200, overflowY: 'auto' }}>
        {documentFiles?.map((row, idx) => (
          <ListItem
            sx={{ pl: 0 }}
            secondaryAction={
              <IconButton
                size='small'
                sx={{ mr: -2 }}
                disabled={disabled}
                onClick={() => (onDocumentFileRemove !== undefined ? onDocumentFileRemove(row.document_id.toString()) : null)}
              >
                <Delete />
              </IconButton>
            }
            key={`${row.fileName}${idx.toString()}`}
            divider
          >
            <ListItemIcon sx={{ minWidth: '2rem !important' }}>
              <InsertDriveFile />
            </ListItemIcon>
            <Typography component='span' variant='body1' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>
              {row.fileName || t('common:fileDropZone.FileNameMissing')}
              <Typography component='span' variant='caption' sx={{ pl: 1 }} noWrap>
                ({formatBytes(row.fileSize, 0)})
              </Typography>
            </Typography>
          </ListItem>
        ))}
        <>
          {validFiles.map((row, idx) => (
            <ListItem
              key={`${row.name}${idx.toString()}`}
              sx={{ pl: 0 }}
              secondaryAction={
                <IconButton size='small' sx={{ mr: -2 }} onClick={() => removeFile(row.name)} disabled={disabled}>
                  <Delete />
                </IconButton>
              }
              divider
            >
              <ListItemIcon sx={{ minWidth: '2rem !important' }}>
                <UploadFileRounded />
              </ListItemIcon>
              <Typography component='span' variant='body1' sx={{ overflow: 'hidden', textOverflow: 'ellipsis', pl: 1 }}>
                {row.name}
              </Typography>
              <Typography component='span' variant='caption' sx={{ pl: 1 }} noWrap>
                ({formatBytes(row.size, 0)})
              </Typography>
            </ListItem>
          ))}
        </>
      </List>
    );
  };

  return (
    <>
      <Box sx={{ width: '100%' }} onDragOver={dragOver} onDragEnter={dragEnter} onDragLeave={dragLeave} onDrop={fileDrop}>
        <Grid
          container
          sx={{ display: 'flex', ...(orientation === 'horizontal' ? { minHeight: 200, flexDirection: 'row' } : { flexDirection: 'column' }) }}
        >
          <Grid
            item
            xs={orientation === 'horizontal' ? 6 : 12}
            sx={{
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              order: orientation === 'horizontal' ? 1 : 2,
            }}
          >
            <label htmlFor='upload-photo'>
              <input
                style={{ display: 'none' }}
                id='upload-photo'
                name='upload-photo'
                type='file'
                disabled={disabled}
                accept={validMimeTypes.toString()}
                onClick={() => {
                  if (inputRef.current) inputRef.current.value = '';
                }}
                onChange={changeHandler}
              />
              <Button
                variant='outlined'
                disableElevation
                disabled={disabled}
                component='span'
                startIcon={<CloudUpload />}
                sx={{ width: { xs: '100%', md: 'auto' } }}
              >
                {allowMultiple ? t('common:fileDropZone.DropFilesOrClickHere') : t('common:fileDropZone.DropFileOrClickHere')}
              </Button>
            </label>
          </Grid>
          <Grid
            item
            xs={orientation === 'horizontal' ? 6 : 12}
            sx={{
              ...(orientation === 'horizontal' ? { borderLeft: '1px solid #eee', pl: 2 } : null),
              order: orientation === 'horizontal' ? 2 : 1,
            }}
          >
            {showFileHeader && (
              <>
                <Typography variant='h6' sx={{ fontWeight: 700 }} gutterBottom>
                  {t('common:fileDropZone.Files')} <small style={{ fontWeight: 100 }}>({fileCounter()})</small>
                </Typography>
                <Divider />
              </>
            )}
            {renderList()}
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default FileDropZone;

export const ControlledFileDropZone = ({ name, disabled, multiple, documentFiles, ...rest }) => {
  const { control } = useFormContext();
  return (
    <Controller
      render={({ field: { onChange } }) => (
        <FileDropZone allowMultiple={multiple} disabled={disabled} documentFiles={documentFiles} onFilesChange={onChange} {...rest} />
      )}
      name={name}
      control={control}
      defaultValue=''
    />
  );
};
